import { useEffect, useRef, type ReactNode } from "react";
import { LayoutTemplate, LogOut, X } from "lucide-react";

interface SiteDrawerProps {
  open: boolean;
  /** Docked beside the page on wide windows; otherwise it slides over it. */
  docked: boolean;
  onClose: () => void;
  siteName: string;
  /** The owner's card dashboard, only when they also have a card site. */
  cardSiteHref?: string;
  account?: { email: string };
  logoutHref?: string;
  modeControls?: ReactNode;
  /** The area switcher, first in the drawer. */
  top?: ReactNode;
  /** Outline, Library and Brand, shown in the drawer on narrow screens. */
  management?: ReactNode;
}

/**
 * The owner's pages, content, photos and brand. Changes and publishing are in the right panel.
 */
export function SiteDrawer(props: SiteDrawerProps) {
  const { open, docked, onClose, siteName } = props;
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (open && !docked) heading.current?.focus({ preventScroll: true });
  }, [open, docked]);

  return (
    <>
      {open && !docked && <div className="bw-scrim" aria-hidden="true" onClick={onClose} />}
      <nav
        id="bw-site-drawer"
        className="bw-drawer"
        aria-labelledby="bw-drawer-title"
        hidden={!open}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.stopPropagation();
          if (!docked) onClose();
        }}
      >
        <div className="bw-drawer-head" hidden={docked}>
          <h2 id="bw-drawer-title" ref={heading} tabIndex={-1}>{siteName}</h2>
          {!docked && (
            <button type="button" className="bw-btn bw-btn--icon" onClick={onClose} aria-label="Close the menu">
              <X size={20} aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="bw-drawer-body">
          <>
              {props.top}
              {props.modeControls}
              {props.management}
              {props.cardSiteHref && (
                <ul className="bw-menu-group">
                  <li>
                    <a className="bw-menu-item bw-menu-item--quiet" href={props.cardSiteHref}>
                      <LayoutTemplate size={20} aria-hidden="true" />
                      <span>Your card site</span>
                    </a>
                  </li>
                </ul>
              )}
              <section className="bw-menu-account" aria-label="Your account">
                {props.account && (
                  <div className="bw-menu-account-user">
                    <span className="bw-menu-account-avatar" aria-hidden="true">{props.account.email.charAt(0).toUpperCase()}</span>
                    <div className="bw-menu-account-identity">
                      <span className="bw-menu-account-email" title={props.account.email}>{props.account.email}</span>
                      <span className="bw-menu-account-caption">Signed in</span>
                    </div>
                  </div>
                )}
                {props.logoutHref && (
                  <a className="bw-menu-item bw-menu-item--quiet bw-menu-logout" href={props.logoutHref}>
                    <LogOut size={20} aria-hidden="true" />
                    <span>Log out</span>
                  </a>
                )}
              </section>
          </>
        </div>
      </nav>
    </>
  );
}
